/**
 * PrivacyPolicy.js - SIMPLIFIED VERSION
 * Honest pre-alpha privacy policy for Constellation KG
 * Accurate to current implementation, clear about limitations
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MarketingFooter from '../common/MarketingFooter/MarketingFooter';
import './PrivacyPolicy.css';
import { DOCUMENT_VERSIONS, formatDocumentDate } from '../../constants/documentVersions';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  // Handle anchor link clicks with smooth scrolling
  const handleAnchorClick = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.pushState(null, '', `#${targetId}`);
    }
  };

  return (
    <div className="privacy-policy">
      <div className="privacy-policy-container">
        {/* Back Navigation */}
        <button 
          className="back-button"
          onClick={() => navigate('/')}
          aria-label="Back to home"
        >
          <ArrowLeft size={20} />
          Back to Home
        </button>

        {/* Header */}
        <header className="privacy-header">
          <h1>
            <button 
              onClick={() => navigate('/')} 
              className="header-link"
              aria-label="Return to Constellation KG home"
            >
              Constellation KG
            </button>
            {' Privacy Policy'}
          </h1>
          <p className="privacy-meta">
            <strong>Last Updated:</strong> {formatDocumentDate(DOCUMENT_VERSIONS.privacy)}<br />
            <strong>Effective Date:</strong> December 1, 2025<br />
            <strong>Status:</strong> Beta
          </p>
        </header>

        {/* Commitment Section */}
        <section className="privacy-section">
          <h2>How We Respect and Protect Your Privacy</h2>
          <p>
            Constellation KG (CKG) is an AI-backed knowledge management tool in active development. Privacy is core to our mission,
not an
afterthought.
          </p>
          
          <div className="key-points">
            <p><strong>What You Should Know:</strong></p>
            <ul>
              <li>We collect only what's necessary to provide our 
services</li>
              <li>We don't sell your data or use tracking/advertising 
cookies</li>
              <li>Your thoughts are private and will never be shared or 
aggregated</li>
              <li>Your focuses and document metadata automatically contribute to CKG's Collective Intelligence — your identity, tags, and thoughts are never exposed</li>
              <li>We're currently in pre-alpha - some features are still 
being built</li>
            </ul>
          </div>

          <div className="info-box">
            <p><strong>Our Goals for Protecting Your Privacy:</strong></p>
            <p>
              Constellation KG is committed to respecting and protecting 
user privacy. We are using the European Union's General Data Protection 
Regulation 
(GDPR) as our North Star. We estimate that at this time (pre-alpha 
testing) we are 60-70% compliant with it and will continue to develop and 
implement 
architectures, processes, and data structures that respect and protect 
your privacy.
            </p>
          </div>
        </section>

        {/* Table of Contents */}
        <nav className="table-of-contents">
          <h2>Table of Contents</h2>
          <ol>
            <li><a href="#section-1" onClick={(e) => handleAnchorClick(e, 
'section-1')}>Information We Collect</a></li>
            <li><a href="#section-2" onClick={(e) => handleAnchorClick(e, 
'section-2')}>Understanding Your Data: Two Layers</a></li>
            <li><a href="#section-3" onClick={(e) => handleAnchorClick(e, 'section-3')}>Collective Intelligence</a></li>
            <li><a href="#section-4" onClick={(e) => handleAnchorClick(e, 'section-4')}>How We Use Your Information</a></li>
            <li><a href="#section-4a" onClick={(e) => handleAnchorClick(e, 'section-4a')}>What Leaves Constellation KG</a></li>
            <li><a href="#section-5" onClick={(e) => handleAnchorClick(e, 'section-5')}>AI Explorer & Third-Party Sharing</a></li>
            <li><a href="#section-6" onClick={(e) => handleAnchorClick(e, 'section-6')}>Phil: Philosophy Explorer</a></li>
            <li><a href="#section-7" onClick={(e) => handleAnchorClick(e, 'section-7')}>Data Storage & Security</a></li>
            <li><a href="#section-8" onClick={(e) => handleAnchorClick(e, 'section-8')}>Cookies & Tracking</a></li>
            <li><a href="#section-9" onClick={(e) => handleAnchorClick(e, 'section-9')}>Your Rights & Data Control</a></li>
            <li><a href="#section-10" onClick={(e) => handleAnchorClick(e, 'section-10')}>Changes to This Policy</a></li>
            <li><a href="#section-11" onClick={(e) => handleAnchorClick(e, 'section-11')}>Contact Us</a></li>
          </ol>
        </nav>

        {/* Section 1 */}
        <section id="section-1" className="privacy-section">
          <h2>1. Information We Collect</h2>
          
          <h3>1.1 Account Information</h3>
          <p>When you create an account:</p>
          <ul>
            <li><strong>Email address</strong> (for login and account 
recovery)</li>
            <li><strong>Password</strong> (securely hashed, never stored in plain text)</li>
            <li><strong>First and last name</strong> (for 
personalization)</li>
          </ul>
          <p>
            We assign each account a unique identifier (UUID) that is used 
internally throughout the system instead of your email or name. This 
allows us to 
protect your privacy by making it more difficult to associate a focus or a 
thought with any particular user.
          </p>

          <h3>1.2 Waitlist Information</h3>
          <p>If you join our waitlist:</p>
          <ul>
            <li><strong>Email address and name</strong></li>
            <li><strong>Waitlist status and approval date</strong></li>
          </ul>
          <p>
            When approved, you will receive a one-time welcome link by email that
            expires after 48 hours. Your account is created at the time of
            approval; the welcome link is used to verify your email and set your
            password on first login.
          </p>

          <h3>1.3 Knowledge Entries</h3>
          <p>When you capture knowledge:</p>
          <ul>
            <li><strong>Document information</strong> — the title, URL, and
publication date of the source, along with any site or publication
name available from the page's metadata. This is publicly available
information about the source, not about you.</li>
            <li><strong>A focus</strong> (a passage of text you paste into 
CKG's browser extension)</li>
            <li><strong>A thought</strong> (your personal reflection on 
the focus)</li>
            <li><strong>Tags</strong> (keywords you assign to the 
entry)</li>
            <li><strong>Timestamps</strong> (system created creation and 
modification dates)</li>
          </ul>

          <h4>Voice Input for Thoughts</h4>
          <p>
            When you use the microphone feature in Constellation KG (in 
the browser extension, My Journal, or AI Explorer), we prioritize your 
privacy by 
preferring on-device speech recognition:
          </p>
          
          <p><strong>Our Privacy-First Approach:</strong></p>
          <ul className="privacy-features">
            <li><strong>On-device processing preferred:</strong> In Chrome 
(version 139+), speech is converted to text entirely on your device. Audio 
never 
leaves your computer.</li>
            <li><strong>No audio transmitted to CKG:</strong> Regardless 
of which browser you use, audio is never sent to or stored by 
Constellation KG. We 
receive only the transcribed text.</li>
            <li><strong>No audio stored:</strong> Speech is converted to 
text in real-time and immediately discarded.</li>
            <li><strong>Only text is saved:</strong> The transcribed text 
(what you said, converted to words) is saved as part of your knowledge 
entry—never 
the audio itself.</li>
          </ul>

          <p><strong>Browser-Specific Behavior:</strong></p>
          <ul>
            <li><strong>Chrome 139+:</strong> Fully private, on-device 
speech recognition. This is our recommended browser for voice input.</li>
            <li><strong>Older Chrome versions:</strong> If on-device 
recognition is unavailable, speech may be processed by Google's 
servers.</li>
            <li><strong>Safari:</strong> Speech is processed by Apple's 
servers (Apple's standard speech recognition service).</li>
            <li><strong>Firefox:</strong> Speech recognition is not 
supported.</li>
          </ul>

          <p>
            We specifically chose to implement on-device recognition to 
maximize your privacy. For the most private experience, we recommend using 
Chrome 
version 139 or later.
          </p>

          <h3>1.4 System Information</h3>
          <p>Minimal technical data:</p>
          <ul>
            <li><strong>Authentication tokens</strong> (stored as 
HTTP-only cookies)</li>
            <li><strong>Login attempts</strong> (for security 
monitoring)</li>
          </ul>

          <h3>1.5 What We DON'T Collect</h3>
          <ul className="not-collected">
            <li>Browsing habits outside of knowledge entries that you make using the extension</li>
            <li>Analytics or tracking cookies</li>
            <li>Advertising identifiers</li>
            <li>Device fingerprints</li>
            <li>Location data</li>
            <li>IP addresses (not logged or stored)</li>
            <li>Social media connections</li>
            <li>Voice recordings or audio samples</li>
            <li>Speech patterns or voiceprints</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section id="section-2" className="privacy-section">
          <h2>2. Understanding Your Data: Two Layers</h2>
          <p>
            CKG uses a two-layer architecture that separates what's 
private (your thoughts) from what may be used as a system resource 
(document metadata, 
focuses, and tags).
          </p>

          <h3>2.1 The Private Layer: Your Thoughts</h3>
          <div className="highlight-box">
            <p><strong>Your thoughts are yours alone.</strong></p>
          </div>
          
          <p>When you write a thought about a focus, that reflection is 
accessible only to you:</p>
          <ul className="privacy-features">
            <li>Never shared with other users</li>
            <li>Never visible to anyone but you</li>
          </ul>
          <p>
            Your thoughts are stored separately from the other elements of
            knowledge entries (document metadata, focuses, and tags). At every
            level of the application, access to your thoughts is strictly scoped
            to your user ID — no other user, and no automated system process, can
            retrieve your thoughts. Thoughts are never used for system-wide
            analysis or for CKG's Collective Intelligence. The database itself is
            encrypted at rest. Access to the underlying database is restricted to
            authorized staff; field-level encryption of thought content is on our
            roadmap to better protect your privacy.
          </p>

          <p>Your thoughts remain private whether you type them or speak 
them. When using voice input:</p>
          <ul className="privacy-features">
            <li>In supported browsers (Chrome 139+), speech-to-text 
conversion happens entirely on your device</li>
            <li>In other browsers, speech may be processed by the browser 
vendor (Google for older Chrome, Apple for Safari)—but never by CKG</li>
            <li>CKG receives only the resulting text, never the audio</li>
            <li>Your voice is never recorded, transmitted to CKG, or 
stored by CKG</li>
          </ul>

          <div className="example-box">
            <p><strong>Example:</strong></p>
            <ul>
              <li><strong>Focus:</strong> "Groves may offer an alternative 
model of sacred space for traditional religions and neopagans alike."</li>
              <li><strong>Thought:</strong> "In reading this, I think 
of..." ← <strong>THIS IS PRIVATE</strong></li>
            </ul>
          </div>

          <h3>2.2 The Public Layer: Focuses & Tags</h3>
          <p><strong>Focuses and tags are system assets</strong> that 
describe what you're engaging with:</p>
          
          <ul>
            <li><strong>Document metadata</strong> (title, URL, 
publication date) - already public information</li>
            <li><strong>Focuses</strong> - a passage of publicly available 
text that you have copied from the page you're reading and pasted into 
Constellation 
KG's browser extension so that it becomes part of your knowledge base</li>
            <li><strong>Tags</strong> - descriptive labels</li>
          </ul>

          <p><strong>Why these are different:</strong></p>
          <ul>
            <li>They describe <strong>what</strong> you're engaging with 
(objective)</li>
            <li>Not <strong>what you think</strong> about it 
(subjective)</li>
            <li>They power CKG's Collective Intelligence — enabling you to discover
the focuses that others have found interesting without revealing your
identity or your opinions</li>
            <li>They are not associated with your name but rather with 
your UUID</li>
          </ul>

          <h3>2.3 Privacy by Design</h3>
          <p>This two-layer separation is architectural, not just 
policy:</p>
          <ol>
            <li><strong>Clear boundaries:</strong> Thoughts are stored 
separately and only accessed for your specific use (searches, AI queries), 
never for 
system-wide analysis or aggregation.</li>
            <li><strong>Maximum protection:</strong> Thoughts are stored 
separately from document metadata, focuses, and tags</li>
            <li><strong>Access controls:</strong> Access to the underlying
database is restricted to authorized staff. Field-level encryption
of thought content is on our roadmap.</li>
          </ol>
        </section>

        {/* Section 3 - Collective Intelligence */}
        <section id="section-3" className="privacy-section">
          <h2>3. Collective Intelligence</h2>

          <p>
            Constellation KG's Collective Intelligence (CI) lets you benefit from
            the research other people do and lets them benefit from your research
            without ever compromising their or your privacy. In short, every CKG
            user is potentially your research assistant just as you are potentially
            theirs.
          </p>
          <p>
            This section explains exactly what goes into CI, what stays out, and
            what other users can see.
          </p>

          <h3>3.1 What CI Is (and Isn't)</h3>
          <p>When you capture a knowledge entry, two things automatically become
          part of CKG's Collective Intelligence:</p>
          <ul>
            <li><strong>Document metadata</strong> — the title and URL of the
            source you're reading</li>
            <li><strong>Your focus</strong> — the passage of text you copied from
            that source</li>
          </ul>

          <div className="highlight-box">
            <p><strong>Your thoughts never appear in Collective
            Intelligence.</strong></p>
          </div>

          <p>
            Your written reflections — whether expressed as a thought in a
            knowledge entry or as an entry in My Journal — are part of your
            private layer (see Section 2). Your thoughts are yours alone. Thoughts
            are never contributed to (or searchable through) the CI, never visible
            to other users, and never used in any system-wide analysis.
          </p>

          <h3>3.2 How CI Uses Your Tags</h3>
          <p>
            When you tag a knowledge entry, those tags help CI understand what a
            focus is about — making it more findable when other users search the
            collective pool. However, <strong>your tags are never displayed to
            other users.</strong> They work behind the scenes, improving retrieval
            without exposing what labels you chose to apply.
          </p>
          <p>
            If you later add personal tags to an entry via My Knowledge Entries,
            those are stored separately and privately on your account. They are
            never shared, and they play no role in CI at all.
          </p>

          <h3>3.3 What Other Users See</h3>
          <p>
            When a CKG user searches Collective Intelligence, they may encounter a
            focus you contributed. Here's what they see — and what they don't:
          </p>

          <p><strong>Visible to other users:</strong></p>
          <ul>
            <li>The focus text itself (the passage from the original source)</li>
            <li>The document metadata (title and URL of the source)</li>
            <li>How many CKG users have captured this exact focus — but only when
            that number reaches 10 or more. Below that threshold, no count is
            shown. This protects against situations where a small count could
            inadvertently narrow down who contributed a focus.</li>
          </ul>

          <p><strong>Never visible to other users:</strong></p>
          <ul className="not-collected">
            <li>Who contributed the focus — you are never identified</li>
            <li>Your tags</li>
            <li>Your thoughts</li>
            <li>Any other details from your knowledge entry</li>
          </ul>

          <p>
            The content visible in CI — the focus and its source — is publicly
            available text that originated on the web. CKG is surfacing it in a
            new context, but not revealing anything you wrote or anything about
            you.
          </p>

          <h3>3.4 Participation Is Automatic</h3>
          <p>
            CI participation is a core part of how CKG works. Every knowledge
            entry you create contributes its document metadata and focus to the
            collective pool. At this time, there is no opt-out for individual
            entries.
          </p>
          <p>
            We want to be honest about this. We believe the trade-off is fair —
            the content contributed is publicly available text, your identity is
            never exposed, and your private reflections are never touched — but we
            think you should know that participation is automatic rather than
            discover it later.
          </p>

          <div className="info-box">
            <p><strong>To summarize what CI does and doesn't touch:</strong></p>
            <ul>
              <li>✓ Document metadata (title, URL) — contributed to CI</li>
              <li>✓ Your focus — contributed to CI</li>
              <li>✓ Your tags — used internally for retrieval, never revealed</li>
              <li>✗ Your thoughts — never in CI, always private</li>
              <li>✗ Your journal entries — never in CI, always private</li>
              <li>✗ Your identity — never exposed</li>
            </ul>
          </div>
        </section>

        {/* Section 4 */}
        <section id="section-4" className="privacy-section">
          <h2>4. How We Use Your Information</h2>

          <h3>4.1 To Provide Services</h3>
          <ul>
            <li>Authenticate your login and maintain your session</li>
            <li>Store and organize your knowledge entries</li>
            <li>Enable search across your knowledge base</li>
            <li>Provide AI-powered analysis (see Section 5)</li>
            <li>Send essential service notifications</li>
          </ul>

          <h3>4.2 Security Monitoring</h3>
          <p>We use minimal security logging to prevent abuse:</p>
          
          <p><strong>What We Log:</strong></p>
          <ul className="privacy-features">
            <li>Login action (success/failure)</li>
            <li>Timestamp</li>
            <li>Security risk flag (boolean only)</li>
          </ul>

          <p><strong>What We DON'T Log:</strong></p>
          <ul className="not-collected">
            <li>IP addresses</li>
            <li>User agent strings</li>
            <li>Browser fingerprints</li>
            <li>Geographic location</li>
          </ul>

          <p>
            Our logging system automatically redacts passwords, tokens,
and masks email addresses (user@example.com → u***@example.com).
          </p>

          <p>
            The one automated limit that affects your access is a weekly query
            cap for AI Explorer, which is determined solely by your subscription
            tier — not by any analysis of your content or behavior.
          </p>

        </section>

        {/* Section 4A - What leaves CKG */}
        <section id="section-4a" className="privacy-section">
          <h2>What leaves Constellation KG</h2>

          <p>
            Some of your content is sent to outside services so CKG can work.
            Nothing that happens automatically produces writing — automatic
            processing returns numbers and labels. The only outside service that
            generates prose is one you start yourself.
          </p>

          <div className="highlight-box">
            <p>
              Before anything is sent, two things are true of every request. No
              identifier travels with it: not your name, not your email, not your
              username, not your internal ID. Content goes out without an owner
              attached. And CKG scans the text first for structured sensitive
              data — card numbers, Social Security numbers, email addresses,
              phone numbers, API keys — and removes what it finds. That scan
              catches patterns, not context; it cannot recognize that a sentence
              identifies you by describing your life. What you write in a thought
              is sent as you wrote it.
            </p>
          </div>

          <h3>When you save an entry</h3>
          <p>
            Saving a knowledge entry or journal entry sends its text to OpenAI
            twice: once to convert it into a numerical representation that makes
            search more flexible and forgiving, so you're not limited to exact
            matches of your search terms; and once to assign it a tone label.
            This is sentiment analysis, and it's what lets you ask AI Explorer
            questions about how you feel about something rather than only what
            you've collected on it — "Where am I most conflicted about AI in
            education?"
          </p>
          <p>
            What comes back is a set of numbers and a single word. CKG stores
            those and discards the rest. No prose is generated and nothing is
            shown to you as writing.
          </p>
          <div className="warning-box">
            <p>
              This happens on every entry you create, whether or not you ever use
              AI Explorer.
            </p>
          </div>

          <h3>When you ask AI Explorer a question</h3>
          <p>
            AI Explorer sends the knowledge and journal entries most relevant to
            your question — focuses, thoughts, tags, titles, and shortened URLs —
            to Anthropic, which returns the analysis you read. If you attach an
            image to a journal entry, that image goes to Anthropic to be
            described. Neither process happens unless you start it. As with
            everything CKG sends outward, no name, email, or account identifier
            goes with it.
          </p>
        </section>

        {/* Section 5 */}
        <section id="section-5" className="privacy-section">
          <h2>5. AI Explorer & Third-Party Sharing</h2>

          <h3>5.1 What AI Explorer Does</h3>
          <p>
            In using AI Explorer, you send a subset of your knowledge base 
that relates to the question you asked to a third-party AI (Anthropic /
Claude).
          </p>
          <p>AI Explorer helps you:</p>
          <ul>
            <li>Analyze themes across your entries</li>
            <li>Identify connections between ideas</li>
            <li>Organize content into conceptual clusters</li>
            <li>Answer questions about your knowledge base</li>
          </ul>
          <p className="emphasis-text">
            <strong>This feature is grounded in your entries, with every claim traceable to a source.</strong>
          </p>

          <p><strong>Synthesis Storage:</strong></p>
          <p>
            When you use AI Explorer, the resulting analysis — what we 
call a "synthesis" — is stored in your account. A synthesis includes the 
themes AI Explorer found, citations from your entries, and excerpts from 
what you wrote. These syntheses serve double duty: they're a record of 
your exploration, and they're also the raw material that Phil (Section 6) 
uses to start conversations with you. Syntheses remain in your account 
even after Phil has used them — they're not deleted.
          </p>
          <ul className="privacy-features">
            <li>Syntheses are private to your account, just like your 
knowledge entries</li>
            <li>They contain derived content (themes and patterns), not 
copies of your full entries</li>
            <li>They're never shared with other users or used for 
system-wide analysis</li>
          </ul>

          <h3>5.2 ⚠️ Important: What Gets Shared</h3>
          <div className="warning-box">
            <p>
              <strong>AI Explorer is our most privacy-sensitive 
feature.</strong> When you use it, we transmit data to third-party AI 
providers.
            </p>
          </div>

          <p><strong>What Gets Shared:</strong></p>
          <ul>
            <li>Your knowledge entry content (focuses and thoughts)</li>
            <li>Document titles and tags</li>
            <li>Your natural language query</li>
          </ul>

          <p><strong>Who Receives This:</strong></p>
          <ul>
            <li><strong>Anthropic</strong> (Claude API) — for AI Explorer 
analysis and Phil conversations</li>
            <li><strong>OpenAI</strong> — used for two purposes: <strong>embeddings</strong> (converting your text into searchable vectors, applied to knowledge entries, journal entries, and AI Explorer syntheses); and <strong>sentiment analysis</strong> (understanding the general tone of your entries — CKG stores only a label like "reflective" or "critical," not the text itself). OpenAI is not used for AI Explorer analysis. Your name, email, and unmodified document URLs are not included.</li>
          </ul>
          <p>No user identifier is sent to any third-party AI service. Anthropic is the only AI provider that generates analysis, and it receives no user identifier.</p>

          <p>
            <strong>Resend</strong> is used to deliver transactional emails —
            account verification, welcome messages, and security notifications.
            Resend receives your email address for delivery purposes only. CKG
            does not use Resend for marketing emails or audience tracking.
          </p>

          <p><strong>Data Retention by AI Providers:</strong></p>
          <ul>
            <li><strong>Anthropic:</strong> Retained for ~30 days for 
security and abuse monitoring. Not used for training AI models (per 
Anthropic's API policies).</li>
            <li><strong>OpenAI:</strong> Embedding inputs are not used for 
training (per OpenAI's API data usage policy). Review their policies for 
current retention details.</li>
            <li>OpenAI receives no user identifier; Anthropic receives no user identifier. Text content is transmitted (to OpenAI for embeddings and sentiment analysis; to Anthropic for AI Explorer analysis and journal image descriptions). Phil is not yet available and sends nothing today.</li>
          </ul>
          
          <p>
            For more information, review Anthropic's <a 
href="https://www.anthropic.com/legal/privacy" target="_blank" 
rel="noopener noreferrer">Privacy 
Policy</a> and <a href="https://www.anthropic.com/legal/commercial-terms"
target="_blank" rel="noopener noreferrer">Commercial Terms of Service</a>,
as well as OpenAI's <a href="https://openai.com/policies/privacy-policy" 
target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a 
href="https://openai.com/policies/business-terms" target="_blank" 
rel="noopener noreferrer">Business Terms</a>.
          </p>

          <p><strong>What's NOT Shared:</strong></p>
          <ul className="privacy-features">
            <li>Your email address or name</li>
            <li>Full document URLs — we strip tracking parameters and tokens before source URLs reach any AI provider. Only the base address (domain and path) is included for attribution context.</li>
          </ul>

          <p><strong>An additional safeguard:</strong> Before any text reaches an AI provider, CKG automatically scans for and removes common sensitive patterns — credit card numbers, email addresses, Social Security numbers, phone numbers, and API keys or tokens. This is a safety net, not a guarantee. It catches structured patterns, but it can't detect every possible form of personal information embedded in free-form text. Our recommendation: don't paste information you wouldn't want analyzed into your knowledge base.</p>

          <h3>5.3 Voice Input in AI Explorer</h3>
          <p>
            You can use voice input to ask questions in AI Explorer. The 
same privacy protections apply as described in Section 1.3:
          </p>
          <ul className="privacy-features">
            <li>Your voice is processed locally (Chrome 139+) or by your 
browser vendor—never by CKG</li>
            <li>Only the transcribed question text is sent to the AI 
provider</li>
            <li>No audio is ever transmitted or stored</li>
          </ul>

          <h3>5.4 CKG's Use of Third-Party AIs</h3>
          <p>
            Third-party AIs allow us to test and refine prompts with 
Constellation KG's data structure faster and at a lower cost than 
implementing and 
integrating a new model. CKG's interaction with third-party AIs is an area 
of active research and prototyping. If how we use AI changes, those 
changes will be 
updated here.
          </p>

          <h3>5.5 Your Choice</h3>
          <p><strong>AI Explorer and Phil are optional:</strong></p>
          <ul className="privacy-features">
            <li>Use CKG without AI Explorer (most features work 
independently)</li>
            <li>Use AI Explorer understanding the current privacy 
trade-off</li>
            <li>Note: Phil (Section 6) relies on AI Explorer syntheses to 
start conversations. If you choose not to use AI Explorer, Phil won't have 
conversation material to work with.</li>
          </ul>
          <p>
            This choice covers AI Explorer and Phil only. It does not cover the
            automatic processing that runs when you save an entry — see
            <strong> What leaves Constellation KG</strong>, above, for what is
            sent regardless of whether you use AI Explorer.
          </p>
        </section>

        {/* Section 6 - Phil */}
        <section id="section-6" className="privacy-section">
          <h2>6. Phil: Philosophy Explorer</h2>

          <div className="warning-box">
            <p><strong>Phil, CKG's conversational feature, is not yet
            available.</strong> It is in restricted development use and no user
            content reaches it. When Phil becomes available, this policy will be
            updated to describe exactly what it sends and where, before you can
            use it.</p>
          </div>

          <p>
            Phil is planned as a conversational feature for engaging with your
            own knowledge base. Because it is not available, nothing in this
            policy's description of automatic or user-initiated third-party
            processing applies to Phil today.
          </p>

          <p>
            Users will be notified when Phil becomes available.
          </p>
        </section>

        {/* Section 7 */}
        <section id="section-7" className="privacy-section">
          <h2>7. Data Storage & Security</h2>

          <h3>7.1 Where Your Data Lives</h3>
          <ul>
            <li><strong>Primary Database:</strong> Cloud-hosted in the United States, encrypted at rest</li>
            <li><strong>Session Storage:</strong> Cloud-hosted, encrypted in transit and at rest</li>
            <li><strong>Application Logs:</strong> Stored on our servers. Logs contain only operational metadata (response times, error codes) — never your thoughts, journal entries, or conversation content. Authentication logs automatically redact passwords and mask email addresses.</li>
          </ul>

          <h3>7.2 Security Measures</h3>
          <p><strong>Account Security:</strong></p>
          <ul>
            <li>Passwords securely hashed using industry-standard algorithms (never stored in plain text)</li>
            <li>Account lockout after 5 failed login attempts — your account is
locked for 15 minutes, after which you can try again. A rate limiter
provides an additional layer of protection against automated login
attempts.</li>
            <li>Session tokens stored as HTTP-only, secure cookies</li>
            <li>Logging out ends your session immediately. Changing your password
ends your current session and requires you to log in again. Your access
token expires automatically after 10 hours, enforced by our servers on
every request.</li>
          </ul>

          <p><strong>Data Protection:</strong></p>
          <ul>
            <li>HTTPS/TLS encryption for all data in transit</li>
            <li>Your data is stored on MongoDB Atlas, which provides AES-256
encryption at rest as a platform feature. This is a hosting provider
guarantee, not an application-layer control. Field-level encryption
of sensitive content such as thoughts is on our roadmap.</li>
            <li>Principle of least privilege for system access</li>
          </ul>

          <p><strong>Privacy Architecture:</strong></p>
          <ul>
            <li>UUID references throughout (not email/name)</li>
            <li>Separation of user identity from knowledge content</li>
            <li>Minimal data collection by default</li>
            <li>Automatic PII redaction in logs</li>
          </ul>

          <h3>7.3 Voice Input Privacy</h3>
          <p>
            The microphone feature in Constellation KG prioritizes 
on-device speech recognition to protect your privacy:
          </p>
          
          <p><strong>Chrome (version 139+) — Recommended for 
Privacy:</strong></p>
          <ul className="privacy-features">
            <li><strong>Local processing:</strong> Speech is converted to 
text directly on your computer</li>
            <li><strong>No transmission:</strong> Audio data is never sent 
to CKG servers or any third party</li>
            <li><strong>Privacy by design:</strong> We specifically check 
for and prefer on-device recognition</li>
          </ul>

          <p><strong>Other Browsers:</strong></p>
          <ul>
            <li><strong>Older Chrome:</strong> May use Google's cloud 
speech recognition if on-device is unavailable</li>
            <li><strong>Safari:</strong> Uses Apple's speech recognition 
service</li>
            <li><strong>Firefox:</strong> Speech recognition is not 
supported</li>
          </ul>

          <p><strong>In All Cases:</strong></p>
          <ul className="privacy-features">
            <li>Audio is never transmitted to or stored by CKG</li>
            <li>We receive only the transcribed text</li>
            <li>Audio exists only momentarily during real-time 
transcription</li>
          </ul>

          <p>
            We chose this approach to maximize privacy while maintaining 
functionality across browsers. When possible, we recommend using Chrome 
139+ for fully 
private, on-device speech recognition.
          </p>
          
          <p>
            This aligns with our principle of collecting only what's 
necessary. We need your thoughts to build your knowledge base; we don't 
need to hear you 
say them.
          </p>
        </section>

        {/* Section 8 */}
        <section id="section-8" className="privacy-section">
          <h2>8. Cookies & Tracking</h2>

          <h3>8.1 Cookies We Use</h3>
          <p>We use only essential cookies:</p>
          
          <p><strong>Access Token Cookie</strong></p>
          <ul>
            <li>HTTP-only and Secure — not accessible to JavaScript</li>
            <li>SameSite protected against cross-site request forgery</li>
            <li>The cookie itself has no expiry date and is removed when you
close your browser. The token inside it stops being accepted 10 hours after
it is issued; that limit is enforced by our servers, not by the browser.</li>
            <li>Cleared immediately when you log out</li>
          </ul>

          <p><strong>Refresh Token Cookie</strong></p>
          <ul>
            <li>HTTP-only and Secure — not accessible to JavaScript</li>
            <li>SameSite protected against cross-site request forgery</li>
            <li>The cookie is removed by your browser after 7 days. The token
inside it remains valid for 30 days from when it was issued, so a copy of that
token obtained outside the cookie would continue to work for the remainder of
those 30 days.</li>
            <li>Cleared immediately when you log out</li>
          </ul>

          <h3>8.2 What We DON'T Use</h3>
          <ul className="not-collected">
            <li>Analytics cookies (no Google Analytics, etc.)</li>
            <li>Advertising cookies</li>
            <li>Social media tracking pixels</li>
            <li>Third-party cookies of any kind</li>
            <li>Browser fingerprinting</li>
          </ul>
          <p className="emphasis-text">
            <strong>We do not track your activity outside CKG or use your 
data for advertising.</strong>
          </p>
        </section>

        {/* Section 9 */}
        <section id="section-9" className="privacy-section">
          <h2>9. Your Rights & Data Control</h2>

          <p>
            CKG is committed to giving you meaningful control over your data.
            Here is the current state of your rights as a CKG user.
          </p>

          <h3>What you can do right now</h3>

          <p>
            <strong>Export your knowledge base</strong> — You can download your
            knowledge entries (including your thoughts) and journal entries at
            any time in JSON or CSV format from your account page. AI Explorer
            analyses are broken into thematic sections and stored for future use
            by Phil. These themes are AI-generated from your knowledge base and
            are treated as part of your private layer — they are never visible
            to other users and are never linked back to you in any shared
            context. AI Explorer's analyses can be downloaded as PDFs when you
            run a query but are not stored as a single query-analysis document
            by Constellation KG.
          </p>

          <p>
            <strong>Delete individual thoughts</strong> — You can delete the
            thought from any knowledge entry without removing the entry itself.
            The document title, URL, and focus will remain in your knowledge
            base and in CKG's Collective Intelligence. Your thought is
            permanently removed. The Collective Intelligence does not identify
            who submitted a focus — only that someone found it interesting
            enough to capture as part of a knowledge entry.
          </p>

          <p>
            <strong>Delete knowledge entries</strong> — You can delete a
            knowledge entry entirely. This removes your personal connection to
            the focus — your thought, your tags, and your association with the
            entry are permanently deleted. The underlying focus and document
            metadata remain as shared system assets in the Collective
            Intelligence, but they are no longer associated with you.
          </p>

          <p>
            <strong>Update your profile</strong> — You can update your name and
            email address at any time from your account page.
          </p>

          <h3>What's still in development</h3>

          <p>
            <strong>Account deletion</strong> — The ability to delete your
            entire account and all associated data is coming soon. In the
            meantime, you can request account deletion by emailing <a
            href="mailto:info@constellationkg.com">info@constellationkg.com</a>
            with "Privacy Request" in the subject line. We will respond within
            72 hours.
          </p>

          <p>
            <strong>Privacy requests</strong> — For any privacy-related request
            — including requests to access, correct, restrict, or delete your
            data — email <a
            href="mailto:info@constellationkg.com">info@constellationkg.com</a>
            with "Privacy Request" in the subject line. We will respond within
            72 hours.
          </p>

          <h3>A note on GDPR and CCPA</h3>

          <p>
            CKG uses GDPR and CCPA as guiding best practices even though we are
            only in a small-scale alpha test. We are not yet fully compliant
            with either framework but few companies are, especially at our
            stage. We estimate that we are 60–70% compliant with GDPR and are
            continuing to work toward the maximum degree of compliance possible.
          </p>
        </section>

        {/* Section 10 */}
        <section id="section-10" className="privacy-section">
          <h2>10. Changes to This Policy</h2>

          <p>
            CKG is in active development and this policy will evolve as the
            platform does. When we make material changes, we will notify you
            via an in-app notice.
          </p>

          <p>
            Continued use after a policy change constitutes acceptance. If you
            have concerns about a change, contact us at <a
            href="mailto:info@constellationkg.com">info@constellationkg.com</a>.
          </p>

          <h3>Children's Privacy</h3>
          <p>
            CKG is not intended for users under 13 (or 16 in the EU). We don't
            knowingly collect data from children. If you believe a child has
            created an account, contact us immediately at <a
            href="mailto:info@constellationkg.com">info@constellationkg.com</a>.
          </p>
        </section>

        {/* Section 11 */}
        <section id="section-11" className="privacy-section">
          <h2>11. Contact Us</h2>
          
          <p><strong>Privacy Questions or Requests:</strong></p>
          <p>
            Email: <a 
href="mailto:info@constellationkg.com">info@constellationkg.com</a><br />
            Response Time: Within 72 hours for initial response
          </p>

          <p>
            For data access, deletion, or correction requests, please 
include "Privacy Request" in your subject line and specify what you need.
          </p>
        </section>

        {/* Back to Top Button */}
        <div className="back-to-top-container">
          <button 
            className="back-to-top"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' 
})}
          >
            Back to Top ↑
          </button>
        </div>
      </div>

      {/* Shared Marketing Footer */}
      <MarketingFooter />
    </div>
  );
};

export default PrivacyPolicy;
